// frontend/src/components/CryptoTokenRiverVisualizer.jsx
import React, { useRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import useCryptoTokenRiverVisualizer from "../hooks/useCryptoTokenRiverVisualizer";

/** Plafond d'affichage — miroir de `POPULATION_MAX` côté intégrateur. */
const MAX_TOKENS = 600;

/**
 * Rendu de la Rivière de Tokens — LE BACKEND POSSÈDE LE FLUX.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md.
 *
 * ⚠️ Ce composant inventait toute la scène : `total = 400 + densité * 1200`
 * sphères, positions initiales au `Math.random()`, `velocity = 0.5 +
 * Math.random() * 1.5`, advection en z, puis recyclage à `z = -50` avec des
 * x,y retirés au sort. Il recevait bien un `frames` en propriété — mais la
 * page ne le lui passait JAMAIS, si bien que le seul bloc qui lisait les
 * tokens du backend (pour les colorer) ne s'exécutait pas.
 *
 * Il rejoue désormais le flux servi par `/animate`. Chaque particule affichée
 * est un caractère RÉELLEMENT ÉMIS par la source, sa couleur dérive de ce
 * caractère, et sa vitesse est mesurée sur son déplacement.
 *
 * ⚠️ APPARIEMENT PAR IDENTIFIANT, pas par indice. Les tokens sortent par la
 * tête du tableau et les nouveaux s'ajoutent à la queue : dès qu'un token
 * sort, tous les indices se décalent. Dériver une vitesse par simple
 * différence d'indice donnerait un résultat faux pour TOUTE la population.
 */
function CryptoTokenRiverCore({
  running = true,
  chaos = false,
  glow = true,
  riverSpeedPct = 100,
  densityPct = 100,
  steps = 200,
  onSnapshot = () => {},
  onActiveCountUpdate = () => {},
}) {
  const groupRef = useRef();
  const meshRefs = useRef([]);
  const playhead = useRef(0);
  const tRef = useRef(0);
  const dernierCompte = useRef(-1);

  const { lecteur } = useCryptoTokenRiverVisualizer({
    riverSpeedPct,
    densityPct,
    steps,
  });

  // Une seule géométrie pour 600 particules.
  const geometrie = useMemo(() => new THREE.SphereGeometry(0.12, 8, 8), []);
  useEffect(() => () => geometrie.dispose(), [geometrie]);

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    tRef.current += dt;

    if (!lecteur) return;

    if (running) {
      playhead.current += dt;
    }

    const vue = lecteur.lire(playhead.current);
    if (!vue) return;
    if (vue.bascule || vue.gel) playhead.current = 0;

    const { alpha, dt: frameDt } = vue;
    const A = vue.a;
    const B = vue.b;

    // Rang de chaque identifiant dans la frame précédente — c'est lui qui
    // permet d'apparier deux instants malgré le décalage des indices.
    const rangA = new Map();
    for (let k = 0; k < A.ids.length; k++) rangA.set(A.ids[k], k);

    const positions = [];
    const symboles = [];
    const velocities = [];

    const n = Math.min(B.ids.length, MAX_TOKENS);
    for (let k = 0; k < n; k++) {
      const mesh = meshRefs.current[k];
      if (!mesh) continue;

      const b = B.positions[k];
      const j = rangA.get(B.ids[k]);

      let x = b[0];
      let y = b[1];
      let z = b[2];

      if (j !== undefined) {
        // Présent dans les deux frames : on interpole, et la vitesse est
        // MESURÉE sur le déplacement réel de CE token.
        const a = A.positions[j];
        x = a[0] + (b[0] - a[0]) * alpha;
        y = a[1] + (b[1] - a[1]) * alpha;
        z = a[2] + (b[2] - a[2]) * alpha;
        velocities.push([
          (b[0] - a[0]) / frameDt,
          (b[1] - a[1]) / frameDt,
          (b[2] - a[2]) / frameDt,
        ]);
        positions.push([x, y, z]);
        symboles.push(B.symboles[k]);
      }
      // Les entrants n'ont pas d'antécédent : ils sont AFFICHÉS mais pas
      // mesurés ce tour-ci. Leur inventer une vitesse nulle fausserait
      // l'histogramme des intensités.

      mesh.visible = true;
      mesh.position.set(x, y, z);

      const c = B.colors[k];
      if (c) {
        mesh.material.color.setRGB(c[0], c[1], c[2]);
        mesh.material.emissive.setRGB(
          glow ? c[0] * 0.5 : 0,
          glow ? c[1] * 0.5 : 0,
          glow ? c[2] * 0.5 : 0
        );
      }
    }
    for (let k = n; k < MAX_TOKENS; k++) {
      const mesh = meshRefs.current[k];
      if (mesh) mesh.visible = false;
    }

    if (dernierCompte.current !== n) {
      dernierCompte.current = n;
      onActiveCountUpdate(n);
    }

    // Le chaos reste un effet de CADRE : il fait tanguer le canal entier sans
    // déplacer les tokens les uns par rapport aux autres. Le snapshot lit
    // l'espace objet, il n'en dépend pas.
    if (groupRef.current) {
      if (chaos) {
        groupRef.current.rotation.z = Math.sin(tRef.current * 0.6) * 0.18;
        groupRef.current.rotation.x = Math.sin(tRef.current * 0.35) * 0.12;
      } else {
        groupRef.current.rotation.z = 0;
        groupRef.current.rotation.x = 0;
      }
    }

    if (positions.length > 1) {
      onSnapshot({ positions, symboles, velocities });
    }
  });

  return (
    <group ref={groupRef}>
      <ambientLight intensity={0.4} />
      <pointLight position={[20, 20, 20]} intensity={1.2} color={0x00ffff} />

      {Array.from({ length: MAX_TOKENS }, (_, i) => (
        <mesh
          key={i}
          ref={(el) => (meshRefs.current[i] = el)}
          geometry={geometrie}
          visible={false}
        >
          <meshPhongMaterial
            transparent
            opacity={0.85}
            emissiveIntensity={0.6}
          />
        </mesh>
      ))}

      <EffectComposer>
        <Bloom intensity={1.3} radius={0.6} threshold={0.0} />
      </EffectComposer>
    </group>
  );
}

export default function CryptoTokenRiverVisualizer(props) {
  return (
    <Canvas camera={{ position: [0, 8, 30], fov: 65 }}>
      <CryptoTokenRiverCore {...props} />
      <OrbitControls enableZoom enablePan />
    </Canvas>
  );
}
